/**
 * Payroll Repository
 *
 * FERP CRITICAL: Repository pattern for Payroll API operations
 *
 * FERP Principles:
 * - Repository pattern (NO direct GraphQL calls outside this file)
 * - Backend ID generation (useGenerateId for run IDs)
 * - Data-driven (all payroll data from backend DynamoDB)
 * - Life-critical (SOX/FLSA compliance with complete audit trail)
 * - Domain-agnostic (payroll is workflow metadata configuration)
 *
 * GraphQL Operations:
 * Queries (4):
 * - getPayrollRun: Get run by ID with S3 snapshot
 * - listPayrollRunsV2: List all runs for tenant
 * - listPayrollRunsByStatus: Filter by status (GSI query)
 * - getEmployeeYtd: Get YTD totals (Context primitive)
 *
 * Mutations (8):
 * - createPayrollRun: Create new run (Draft)
 * - startTimeCardReview: Draft → ReviewingTimeCards
 * - triggerPayrollCalculation: ReviewingTimeCards → Calculating
 * - importPayrollResults: Calculating → ReviewingResults
 * - submitPayrollForApproval: ReviewingResults → PendingApproval
 * - approvePayrollRun: PendingApproval → Approved
 * - rejectPayrollRun: PendingApproval → Rejected
 * - requestPayrollRevisions: PendingApproval → RevisionRequested
 * - markPayrollAsPaid: Approved → Paid
 */

import { ApolloClient, gql } from '@apollo/client';

// ============================================================================
// TypeScript Types (Match Rust backend models)
// ============================================================================

export type PayrollRunStatus =
  | 'draft'
  | 'reviewing_time_cards'
  | 'calculating'
  | 'calculation_failed'
  | 'reviewing_results'
  | 'pending_approval'
  | 'revision_requested'
  | 'approved'
  | 'payment_failed'
  | 'paid'
  | 'rejected';

export interface PayrollRun {
  runId: string;
  tenantId: string;
  payPeriodStart: string; // ISO 8601 date
  payPeriodEnd: string;
  paymentDate: string;
  status: PayrollRunStatus;
  employeeCount: number;
  totalGrossPay: string; // Decimal as string
  totalNetPay: string;
  totalDeductions: string;
  totalTaxes: string;
  s3SnapshotKey: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  approvedAt: string | null;
  approvedBy: string | null;
  approvalComments: string | null;
}

export interface EmployeeYTD {
  employeeId: string;
  tenantId: string;
  year: number;
  ytdGross: string;
  ytdFederalTax: string;
  ytdStateTax: string;
  ytdSocialSecurity: string;
  ytdMedicare: string;
  ytdNetPay: string;
  payPeriodCount: number;
  lastUpdated: string;
}

// ============================================================================
// GraphQL Fragments
// ============================================================================

const PAYROLL_RUN_FRAGMENT = gql`
  fragment PayrollRunFields on PayrollRunGql {
    runId
    tenantId
    payPeriodStart
    payPeriodEnd
    paymentDate
    status
    employeeCount
    totalGrossPay
    totalNetPay
    totalDeductions
    totalTaxes
    s3SnapshotKey
    createdAt
    createdBy
    updatedAt
    updatedBy
    approvedAt
    approvedBy
    approvalComments
  }
`;

const EMPLOYEE_YTD_FRAGMENT = gql`
  fragment EmployeeYTDFields on EmployeeYTDGql {
    employeeId
    tenantId
    year
    ytdGross
    ytdFederalTax
    ytdStateTax
    ytdSocialSecurity
    ytdMedicare
    ytdNetPay
    payPeriodCount
    lastUpdated
  }
`;

// ============================================================================
// GraphQL Queries
// ============================================================================

const GET_PAYROLL_RUN = gql`
  query GetPayrollRun($tenantId: String!, $runId: String!) {
    getPayrollRun(tenantId: $tenantId, runId: $runId) {
      ...PayrollRunFields
    }
  }
  ${PAYROLL_RUN_FRAGMENT}
`;

const LIST_PAYROLL_RUNS_V2 = gql`
  query ListPayrollRunsV2($tenantId: String!, $limit: Int) {
    listPayrollRunsV2(tenantId: $tenantId, limit: $limit) {
      ...PayrollRunFields
    }
  }
  ${PAYROLL_RUN_FRAGMENT}
`;

const LIST_PAYROLL_RUNS_BY_STATUS = gql`
  query ListPayrollRunsByStatus($tenantId: String!, $status: String!, $limit: Int) {
    listPayrollRunsByStatus(tenantId: $tenantId, status: $status, limit: $limit) {
      ...PayrollRunFields
    }
  }
  ${PAYROLL_RUN_FRAGMENT}
`;

const GET_EMPLOYEE_YTD = gql`
  query GetEmployeeYtd($tenantId: String!, $employeeId: String!, $year: Int!) {
    getEmployeeYtd(tenantId: $tenantId, employeeId: $employeeId, year: $year) {
      ...EmployeeYTDFields
    }
  }
  ${EMPLOYEE_YTD_FRAGMENT}
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CREATE_PAYROLL_RUN = gql`
  mutation CreatePayrollRun(
    $tenantId: String!
    $runId: String!
    $payPeriodStart: String!
    $payPeriodEnd: String!
    $paymentDate: String!
    $createdBy: String!
  ) {
    createPayrollRun(
      tenantId: $tenantId
      runId: $runId
      payPeriodStart: $payPeriodStart
      payPeriodEnd: $payPeriodEnd
      paymentDate: $paymentDate
      createdBy: $createdBy
    ) {
      ...PayrollRunFields
    }
  }
  ${PAYROLL_RUN_FRAGMENT}
`;

const START_TIME_CARD_REVIEW = gql`
  mutation StartTimeCardReview($tenantId: String!, $runId: String!, $updatedBy: String!) {
    startTimeCardReview(tenantId: $tenantId, runId: $runId, updatedBy: $updatedBy)
  }
`;

const TRIGGER_PAYROLL_CALCULATION = gql`
  mutation TriggerPayrollCalculation($tenantId: String!, $runId: String!, $updatedBy: String!) {
    triggerPayrollCalculation(tenantId: $tenantId, runId: $runId, updatedBy: $updatedBy)
  }
`;

const IMPORT_PAYROLL_RESULTS = gql`
  mutation ImportPayrollResults($tenantId: String!, $runId: String!, $updatedBy: String!) {
    importPayrollResults(tenantId: $tenantId, runId: $runId, updatedBy: $updatedBy)
  }
`;

const SUBMIT_PAYROLL_FOR_APPROVAL = gql`
  mutation SubmitPayrollForApproval($tenantId: String!, $runId: String!, $updatedBy: String!) {
    submitPayrollForApproval(tenantId: $tenantId, runId: $runId, updatedBy: $updatedBy)
  }
`;

const APPROVE_PAYROLL_RUN = gql`
  mutation ApprovePayrollRun(
    $tenantId: String!
    $runId: String!
    $approvedBy: String!
    $comments: String
  ) {
    approvePayrollRun(
      tenantId: $tenantId
      runId: $runId
      approvedBy: $approvedBy
      comments: $comments
    )
  }
`;

const REJECT_PAYROLL_RUN = gql`
  mutation RejectPayrollRun(
    $tenantId: String!
    $runId: String!
    $rejectedBy: String!
    $comments: String!
  ) {
    rejectPayrollRun(
      tenantId: $tenantId
      runId: $runId
      rejectedBy: $rejectedBy
      comments: $comments
    )
  }
`;

const REQUEST_PAYROLL_REVISIONS = gql`
  mutation RequestPayrollRevisions(
    $tenantId: String!
    $runId: String!
    $requestedBy: String!
    $comments: String!
  ) {
    requestPayrollRevisions(
      tenantId: $tenantId
      runId: $runId
      requestedBy: $requestedBy
      comments: $comments
    )
  }
`;

const MARK_PAYROLL_AS_PAID = gql`
  mutation MarkPayrollAsPaid($tenantId: String!, $runId: String!, $updatedBy: String!) {
    markPayrollAsPaid(tenantId: $tenantId, runId: $runId, updatedBy: $updatedBy)
  }
`;

// ============================================================================
// Input Types
// ============================================================================

export interface CreatePayrollRunInput {
  tenantId: string;
  runId: string;
  payPeriodStart: string; // ISO 8601 date (YYYY-MM-DD)
  payPeriodEnd: string;
  paymentDate: string;
  createdBy: string;
}

export interface StatusTransitionInput {
  tenantId: string;
  runId: string;
  updatedBy: string;
}

export interface ApprovalInput {
  tenantId: string;
  runId: string;
  approvedBy: string;
  comments?: string;
}

export interface RejectionInput {
  tenantId: string;
  runId: string;
  rejectedBy: string;
  comments: string;
}

export interface RevisionRequestInput {
  tenantId: string;
  runId: string;
  requestedBy: string;
  comments: string;
}

// ============================================================================
// Payroll Repository Class
// ============================================================================

export class PayrollRepository {
  constructor(private apolloClient: ApolloClient) {}

  // ==========================================================================
  // Queries
  // ==========================================================================

  /**
   * Get payroll run by ID
   * Primitive: WorkflowInstance query
   */
  async getPayrollRun(tenantId: string, runId: string): Promise<PayrollRun | null> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: GET_PAYROLL_RUN,
      variables: { tenantId, runId },
      fetchPolicy: 'network-only', // Always fetch fresh data
    });

    return data?.getPayrollRun || null;
  }

  /**
   * List all payroll runs for tenant
   * Primitive: WorkflowInstance list query
   */
  async listPayrollRuns(tenantId: string, limit?: number): Promise<PayrollRun[]> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: LIST_PAYROLL_RUNS_V2,
      variables: { tenantId, limit },
      fetchPolicy: 'network-only',
    });

    return data?.listPayrollRunsV2 || [];
  }

  /**
   * List payroll runs by status
   * Primitive: WorkflowInstance status filter (GSI query)
   */
  async listPayrollRunsByStatus(
    tenantId: string,
    status: PayrollRunStatus,
    limit?: number
  ): Promise<PayrollRun[]> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: LIST_PAYROLL_RUNS_BY_STATUS,
      variables: { tenantId, status, limit },
      fetchPolicy: 'network-only',
    });

    return data?.listPayrollRunsByStatus || [];
  }

  /**
   * Get employee YTD totals
   * Primitive: Context query
   */
  async getEmployeeYTD(
    tenantId: string,
    employeeId: string,
    year: number
  ): Promise<EmployeeYTD | null> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: GET_EMPLOYEE_YTD,
      variables: { tenantId, employeeId, year },
      fetchPolicy: 'network-only',
    });

    return data?.getEmployeeYtd || null;
  }

  // ==========================================================================
  // Mutations - Workflow State Transitions
  // ==========================================================================

  /**
   * Create new payroll run (Draft state)
   * Primitive: WorkflowInstance creation
   *
   * FERP: Backend generates Snowflake ID (5-layer enforcement prevents client-side)
   */
  async createPayrollRun(input: CreatePayrollRunInput): Promise<PayrollRun> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: CREATE_PAYROLL_RUN,
      variables: input,
    });

    if (!data?.createPayrollRun) {
      throw new Error('Failed to create payroll run');
    }

    return data?.createPayrollRun ?? null;
  }

  /**
   * Start time card review (Draft → ReviewingTimeCards)
   * Primitive: WorkflowInstance status transition
   */
  async startTimeCardReview(input: StatusTransitionInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: START_TIME_CARD_REVIEW,
      variables: input,
    });

    if (!data?.startTimeCardReview) {
      throw new Error('Failed to start time card review');
    }
  }

  /**
   * Trigger payroll calculation (ReviewingTimeCards → Calculating)
   * Primitive: Task execution (TimeTrex trigger)
   */
  async triggerCalculation(input: StatusTransitionInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: TRIGGER_PAYROLL_CALCULATION,
      variables: input,
    });

    if (!data?.triggerPayrollCalculation) {
      throw new Error('Failed to trigger payroll calculation');
    }
  }

  /**
   * Import payroll results (Calculating → ReviewingResults)
   * Primitive: ExecutionResult storage + Context updates (YTD)
   */
  async importResults(input: StatusTransitionInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: IMPORT_PAYROLL_RESULTS,
      variables: input,
    });

    if (!data?.importPayrollResults) {
      throw new Error('Failed to import payroll results');
    }
  }

  /**
   * Submit for approval (ReviewingResults → PendingApproval)
   * Primitive: WorkflowInstance status transition
   */
  async submitForApproval(input: StatusTransitionInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: SUBMIT_PAYROLL_FOR_APPROVAL,
      variables: input,
    });

    if (!data?.submitPayrollForApproval) {
      throw new Error('Failed to submit for approval');
    }
  }

  /**
   * Approve payroll run (PendingApproval → Approved)
   * Primitive: WorkflowInstance status transition
   */
  async approveRun(input: ApprovalInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: APPROVE_PAYROLL_RUN,
      variables: input,
    });

    if (!data?.approvePayrollRun) {
      throw new Error('Failed to approve payroll run');
    }
  }

  /**
   * Reject payroll run (PendingApproval → Rejected - terminal)
   * Primitive: WorkflowInstance status transition
   */
  async rejectRun(input: RejectionInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: REJECT_PAYROLL_RUN,
      variables: input,
    });

    if (!data?.rejectPayrollRun) {
      throw new Error('Failed to reject payroll run');
    }
  }

  /**
   * Request revisions (PendingApproval → RevisionRequested)
   * Primitive: WorkflowInstance status transition
   */
  async requestRevisions(input: RevisionRequestInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: REQUEST_PAYROLL_REVISIONS,
      variables: input,
    });

    if (!data?.requestPayrollRevisions) {
      throw new Error('Failed to request revisions');
    }
  }

  /**
   * Mark as paid (Approved → Paid - terminal success)
   * Primitive: WorkflowInstance status transition
   */
  async markAsPaid(input: StatusTransitionInput): Promise<void> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: MARK_PAYROLL_AS_PAID,
      variables: input,
    });

    if (!data?.markPayrollAsPaid) {
      throw new Error('Failed to mark as paid');
    }
  }
}

/**
 * Factory function to create PayrollRepository
 * Usage: const repository = createPayrollRepository(apolloClient);
 */
export function createPayrollRepository(apolloClient: ApolloClient): PayrollRepository {
  return new PayrollRepository(apolloClient);
}
